import React from 'react';
import { Language } from '../../pages/DevPortal';
import { Server, Database, Cloud } from 'lucide-react';

export default function OverviewSection({ lang }: { lang: Language }) {
    const isEn = lang === 'en';

    return (
        <div className="animate-fade-in">
            <h1 className="text-3xl font-bold text-slate-900 mb-4">
                {isEn ? 'Architecture Overview' : 'Visión General de Arquitectura'}
            </h1>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                {isEn 
                    ? 'Hospital Status is a real-time surgical tracking platform. It uses an edge-compute architecture to provide sub-second latency for patient status updates across multiple hospitals.'
                    : 'Hospital Status es una plataforma de seguimiento quirúrgico en tiempo real. Utiliza una arquitectura edge-compute para proveer latencia sub-segundo en actualizaciones de estado a través de múltiples hospitales.'}
            </p>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-10">
                <h2 className="text-sm font-bold text-slate-900 uppercase tracking-widest mb-6">
                    {isEn ? 'Core Infrastructure' : 'Infraestructura Principal'}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white p-5 rounded-lg border border-slate-100 shadow-sm">
                        <Cloud className="w-6 h-6 text-indigo-500 mb-3" />
                        <h3 className="font-bold text-slate-900 mb-1">Cloudflare Workers</h3>
                        <p className="text-sm text-slate-500">
                            {isEn ? 'Serverless edge computing in 300+ global PoPs. Powers the Hono-based API.' : 'Cómputo serverless en el borde. Potencia la API basada en Hono.'}
                        </p>
                    </div>
                    <div className="bg-white p-5 rounded-lg border border-slate-100 shadow-sm">
                        <Database className="w-6 h-6 text-indigo-500 mb-3" />
                        <h3 className="font-bold text-slate-900 mb-1">Cloudflare D1</h3>
                        <p className="text-sm text-slate-500">
                            {isEn ? 'Distributed SQLite database with automatic read-replication.' : 'Base de datos SQLite distribuida con replicación automática.'}
                        </p>
                    </div>
                    <div className="bg-white p-5 rounded-lg border border-slate-100 shadow-sm">
                        <Server className="w-6 h-6 text-indigo-500 mb-3" />
                        <h3 className="font-bold text-slate-900 mb-1">Server-Sent Events</h3>
                        <p className="text-sm text-slate-500">
                            {isEn ? 'One-way real-time data streaming for TV displays and patient boards.' : 'Streaming unidireccional en tiempo real para pantallas de hospitales.'}
                        </p>
                    </div>
                </div>
            </div>

            <h2 className="text-xl font-bold text-slate-900 mb-4 mt-12">
                {isEn ? 'High Availability (HA) & Disaster Recovery (DR)' : 'Alta Disponibilidad (HA) y Disaster Recovery (DR)'}
            </h2>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-10">
                <p className="text-slate-600 leading-relaxed mb-4">
                    {isEn 
                        ? 'Hospital Status provides built-in Enterprise-grade HA and DR that matches the rigorous standards of comprehensive systems like Oracle Cloud. Our Serverless architecture guarantees 99.99% uptime seamlessly.'
                        : 'Hospital Status provee HA y DR corporativo que cumple con los rigurosos estándares de entornos como Oracle Cloud. Nuestra arquitectura Serverless garantiza 99.99% de Uptime nativamente.'}
                </p>
                <ul className="list-disc list-inside text-slate-600 space-y-2 ml-2">
                    <li>{isEn ? 'Global Replication: Data is replicated across Cloudflare edge nodes globally.' : 'Replicación Global: Los datos se replican activamente a través de los nodos de Cloudflare en todo el mundo.'}</li>
                    <li>{isEn ? 'Automated Backups: D1 databases are backed up automatically to off-site R2 storage.' : 'Respaldos Automatizados: Las bases de datos D1 se respaldan de forma automática hacia almacenamiento R2 inmutable.'}</li>
                </ul>
            </div>

            <h2 className="text-xl font-bold text-slate-900 mb-4">
                {isEn ? 'Integration Middleware (Tasy, Mirth, Iguana)' : 'Middleware de Integración (Tasy, Mirth, Iguana)'}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6">
                {isEn 
                    ? 'We never require modifications to your core HIS. Integrations should route through your hospital\'s established Integration Web Service or Middleware engine. The Middleware simply transforms the HIS HL7/SOAP events into a standard JSON POST to our API.'
                    : 'Nunca requerimos que se modifique el código "core" de su HIS. Las integraciones deben fluir a través del "Integration Web Service" o motor Middleware del hospital. El Middleware simplemente transforma los eventos HL7/SOAP en un rápido POST JSON hacia nuestra API.'}
            </p>
            
        </div>
    );
}
